<template>
  <div class="basic-chat">
    <!-- sidenav -->
    <sidenav
      :custom-class="'bg-white'"
      :class="['position-fixed', 'fixed-start']"
      :style="{ zIndex: this.$store.state.isSidenavOpen ? 100 : '' }"
      :chatbots="chatbots"
      :isDeleteMode="isDeleteMode"
      @bot-init="handleInitChat"
      @bot-selected="getConversationHistory"
      @bot-deleted="handleConfirmDeleteBot"
      @handle-delete-mode="handleDeleteMode"
    >
    </sidenav>
    <div class="container-fluid py-4">
      <div class="mt-5">
        <transition name="fade" mode="out-in">
          <default-card
            v-if="isDefaultCard"
            key="default"
            :attached-files="attachedFiles"
            :model="selectedModel"
            :modelLabel="selectedModelLabel"
            :modelOptions="selectedModelOptions"
            :personality="selectedPersonality"
            :searchOpen="isSearchOpen"
            :webSearch="isWebSearch"
            @remove-file="removeFile"
            @handle-dragover="handleDragOver"
            @handle-drop="handleDrop"
            @handle-selected-model="handleSelectedModel"
            @handle-selected-model-label="handleSelectedModelLabel"
            @handle-selected-reasoning-effort="handleSelectedReasoningEffort"
            @handle-selected-personality="handleSelectedPersonality"
            @handle-selected-personality-label="handleSelectedPersonalityLabel"
            @handel-web-search="handelWebSearch"
            @upload-files="handleFileUpload"
            @input-message="handleSendMessage"
          />
          <chat-card
            v-else
            key="chat"
            :messages="messages"
            :attached-files="attachedFiles"
            :model="selectedModel"
            :modelLabel="selectedModelLabel"
            :modelOptions="selectedModelOptions"
            :personality="selectedPersonality"
            :webSearch="isWebSearch"
            :is-history-loaded="isHistoryLoaded"
            :parse-markdown="parseMarkdown"
            :is-streaming="isStreaming"
            @stop-stream="stopStreaming"
            @remove-file="removeFile"
            @handle-dragover="handleDragOver"
            @handle-drop="handleDrop"
            @handle-office-file-download="handleOfficeFileDownload"
            @handle-selected-model="handleSelectedModel"
            @handle-selected-model-label="handleSelectedModelLabel"
            @handle-selected-reasoning-effort="handleSelectedReasoningEffort"
            @handle-selected-personality="handleSelectedPersonality"
            @handle-selected-personality-label="handleSelectedPersonalityLabel"
            @handel-web-search="handelWebSearch"
            @upload-files="handleFileUpload"
            @send-message="handleSendMessage"
          />
        </transition>
      </div>
    </div>
    <ErrorPopup
      :show="showErrorPopup"
      :message="errorMessage"
      @close="closeErrorPopup"
    />
    <ConfirmDeleteBotPopup
      :show="showConfirmDeleteBotPopup"
      @close="closeConfirmDeleteBotPopup"
      @delete="deleteChatbot"
    />
    <!-- Overlay for processing message -->
    <ProcessingOverlay :show="isCreatingChatbot || isUpdatingChatbot" />
  </div>
</template>

<script>
import { NAV_MASTER } from '@/constants/navMaster'
import HTTPService from '@/services/HTTPService.js'
//import StorageService from '@/services/StorageService.js'
import ErrorPopup from '@/components/ErrorPopup.vue'
import ConfirmDeleteBotPopup from '@/components/ConfirmDeleteBotPopup.vue'
import { v4 as uuidv4 } from 'uuid'
import { cilPaperclip, cilReload, cilSend, cilX } from '@coreui/icons'
import { marked } from 'marked'
import DOMPurify from 'isomorphic-dompurify'
import Sidenav from './components/Sidenav/Index.vue'
import ChatCard from './components/ChatCard.vue'
import DefaultCard from './components/DefaultCard.vue'
import ProcessingOverlay from '@/components/ProcessingOverlay.vue'

import Prism from 'prismjs'
import 'prismjs/components/prism-markup-templating'
import 'prismjs/components/prism-markup'
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-docker'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-c'
import 'prismjs/components/prism-cpp'
import 'prismjs/components/prism-php'
import 'prismjs/components/prism-yaml'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-visual-basic'
import { useAuthStore } from '@/store/auth'

export default {
  components: {
    ProcessingOverlay,
    Sidenav,
    ChatCard,
    DefaultCard,
    ErrorPopup,
    ConfirmDeleteBotPopup,
  },
  props: {
    // bot_id: {
    //   type: String,
    //   required: true,
    // },
    conversation_id: {
      type: String,
      required: false,
    },
    style: {
      type: Object,
      default: () => ({
        botBackgroundColor: '#fff',
        botTextColor: 'black',
        chatbotHeading: 'Hello!',
        chatbotSubHeading: 'Ask me anything',
        firstMessage: 'Hello, what can I help you with?',
        headerBackgroundColor: '#001d61ff',
        headerTextColor: '#FFFFFF',
        image: null,
        inputPlaceholderText: 'What do these say?',
        promptDefaultText:
          'あなたはチャットでユーザを支援するAIアシスタントです。' +
          '見出しにはChatGPT風にアイコンを付加してください。ただし、常に同じアイコン（例：🤖）を使わず、' +
          '回答内容の意味や種類に応じて適切な絵文字アイコンを選択してください。' +
          '説明は📘、手順は🛠、注意点は⚠️、成功例は✅、アイデアは💡、コード解説は💻など、' +
          '文脈に応じて複数のアイコンを使い分けてください。同一回答内でも適切に変化させてください。' +
          'また、出力内容はVue.jsでMarkdownからHTMLに変換される前提で整形してください。' +
          '具体的には以下の点を守ってください：' +
          '・コードブロックは「```」で囲み、言語名を指定してください（例：```javascript）。' +
          '・見出し（#, ##, ###）を適切に使用し、階層構造を明確にしてください。' +
          '・リンクは通常のMarkdown形式で出力してください（例：[ファイル名](/files/sample.pdf)）。' +
          '・表や箇条書きはMarkdownで正しく構文化してください。' +
          '・改行は\\nではなくMarkdownの改行規則を使用してください。' +
          '・特殊文字はエスケープせずに自然なMarkdown記法で出力してください。' +
          'これにより、marked・DOMPurify・Prismによる整形時に正しくレンダリングされます。',
        promptPlaceholderText: 'Enter your prompt here.',
        promptLabelText: 'システムコンテキスト',
        sendButtonBackgroundColor: '#001d61ff',
        userBackgroundColor: '#001d61ff',
        userTextColor: '#FFFFFF',
        assistantName: 'Assistant',
        bot_id: 'preview',
      }),
    },
    name: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      messages: [],
      userInput: '',
      messageIndex: 0,
      conversationId: '',
      cilSend,
      cilPaperclip,
      cilReload,
      cilX,
      user: null,
      selectedModel: 'gpt-5.6-terra', // Default model
      selectedModelLabel: 'gpt-5.6-terra（スピード重視）', // Default model
      selectedReasoningEffort: null,
      selectedModelOptions: [
        { value: 'gpt-5.6-terra', label: 'gpt-5.6-terra（スピード重視）' },
        { value: 'gpt-6.1-sol', label: 'gpt-6.1-sol（思考重視）' },
        { value: 'claude-opus-5-5', label: 'claude-opus-5-5' },
        { value: 'claude-sonnet-5-5', label: 'claude-sonnet-5-5' },
        { value: 'claude-haiku-5-5', label: 'claude-haiku-5-5' },
        // { value: 'o4-mini-deep-research', label: 'o4-mini-deep-research' },
        /*
        {
          value: 'gpt-5.6-sol',
          label: 'Deep Research（調査時間/標準）',
          effort: 'high',
        },
        {
          value: 'gpt-5.6-sol',
          label: 'Deep Research（調査時間/長め）',
          effort: 'xhigh',
        },
       */
        {
          value: 'gpt-6.1-sol',
          label: 'Deep Research',
          effort: 'max',
        },
      ],
      selectedPersonality: '',
      selectedPersonalityLabel: '',
      isSearchOpen: true,
      isWebSearch: false,
      attachedFiles: [],
      promptValue: this.style.promptDefaultText,
      isPromptCollapsed: false, // Stop collapsed by default
      bot_id: '',
      delete_bot_id: '',
      botName: '新規チャットルーム',
      isCreatingChatbot: false,
      isUpdatingChatbot: false,
      ragResult: [],
      fileResults: '',
      showErrorPopup: false,
      showConfirmDeleteBotPopup: false,
      errorMessage: '',
      chatbots: [],
      isDefaultCard: true,
      maxPerFileMB: 50,
      maxPerImageFileMB: 15,
      isHistoryLoaded: false,
      abortController: null,
      isStreaming: false,
      isDeleteMode: false,
    }
  },
  async mounted() {
    console.log('loading..' + this.bot_id)
    try {
      console.log('get user..')
      await this.getChatbots()
      if (this.chatbots.length >= 50) {
        await this.getConversationHistory(this.chatbots[0].id)
      }
    } catch (error) {
      console.error(error)
      // If there's an error or no user found, redirect to the login page
      this.$router.push('/login')
    }

    marked.setOptions({
      silent: true,
      xhtml: true,
      breaks: true,
      gfm: true,
    })

    this.messageIndex = 1
    if (this.conversation_id !== '') {
      this.conversationId = this.conversation_id
    }

    if (this.$route.query.conversationId) {
      this.conversationId = this.$route.query.conversationId
    }

    if (!this.conversationId) {
      this.conversationId = uuidv4()
    } else {
      await this.startConversation()
    }

    document.addEventListener('click', this.handleFileClick)
  },
  computed: {
    getImage() {
      return this.style.image
    },

    getAssistantName() {
      return this.style.assistantName
    },
    getHeading() {
      return this.style.chatbotHeading
    },
    getSubHeading() {
      return this.style.chatbotSubHeading
    },
    getHeadingStyle() {
      return {
        backgroundColor: this.style.headerBackgroundColor,
        color: this.style.headerTextColor,
      }
    },
    getSendButtonStyle() {
      const backgroundColor = this.style.sendButtonBackgroundColor

      return {
        backgroundColor,
        border: 'none',
        padding: '10px',
        marginRight: '10px',
      }
    },
    getInputMessage() {
      return this.style.inputPlaceholderText
    },
    getPromptMessage() {
      return this.style.promptPlaceholderText
    },
    getPromptLabel() {
      return this.style.promptLabelText
    },
    getUserMessageStyle() {
      return {
        backgroundColor: this.style.userBackgroundColor,
        color: this.style.userTextColor,
      }
    },
    getBotMessageStyle() {
      return {
        backgroundColor: this.style.botBackgroundColor,
        color: this.style.botTextColor,
      }
    },
    getAttachButtonStyle() {
      const backgroundColor = this.style.sendButtonBackgroundColor

      return {
        backgroundColor,
        border: 'none',
        padding: '10px',
        marginRight: '5px', // Margin between the buttons
      }
    },
    onModelChange() {
      return null
    },
    getDefaultPrompt() {
      return this.style.promptDefaultText
    },
  },
  methods: {
    stopStreaming() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }

      this.isStreaming = false
    },
    handleInitChat() {
      this.bot_id = ''
      this.conversationId = uuidv4()
      this.messages = []
      this.isDefaultCard = true
    },
    handleFileClick(event) {
      const link = event.target.closest('a[data-filename]')
      if (link) {
        event.preventDefault() // Prevent the default action
        const filename = link.getAttribute('data-filename')
        this.handleFileDownload(filename) // Call your download method
      }
    },
    handleFileDownload(filename) {
      console.log('Clicked', filename) // Debugging
      HTTPService.getfile(filename) // Your existing file download logic
    },
    handleSelectedModel(model) {
      this.selectedModel = model
    },
    handleSelectedModelLabel(label) {
      this.selectedModelLabel = label
    },
    handleSelectedReasoningEffort(effort) {
      this.selectedReasoningEffort = effort
    },
    handleSelectedPersonality(personality) {
      this.selectedPersonality = personality
    },
    handleSelectedPersonalityLabel(label) {
      this.selectedPersonalityLabel = label
    },
    handelWebSearch(isWebSearch) {
      this.isWebSearch = isWebSearch
    },
    handleConfirmDeleteBot(botId) {
      this.delete_bot_id = botId
      this.showConfirmDeleteBotPopup = true
    },
    handleDeleteMode(isDeleteMode) {
      this.isDeleteMode = isDeleteMode
    },
    togglePromptCollapse() {
      this.isPromptCollapsed = !this.isPromptCollapsed
    },
    parseMarkdown(text) {
      if (typeof text !== 'string') {
        return '' // Return the text as is if it's not a string
      }

      text = text.replaceAll('\\n', '\n')
      text = text.replaceAll('```markdown', '```')
      // 外側の () を削除
      text = text.replace(/[（(]\s*(\[[^\]]+\]\([^)]+\))\s*[)）]/g, '$1')

      try {
        const renderer = new marked.Renderer()

        // ----------------------------------------
        // URL判定
        // ----------------------------------------
        const isExternalLink = (url) => {
          return /^https?:\/\//.test(url || '')
        }

        // ----------------------------------------
        // ドメイン取得
        // ----------------------------------------
        const getDomain = (url) => {
          try {
            return new URL(url).hostname.replace(/^www\./, '')
          } catch {
            return url
          }
        }

        // ----------------------------------------
        // link renderer
        // ※ a > div はDOM崩れするので
        // a > span にする
        // ----------------------------------------
        renderer.link = function (href, title, text) {
          // Renderer() seems to return different parameters depending on the version of marked installed...
          const link = {
            href: href.href ?? href,
            text: href.text ?? text,
          }

          // ----------------------------------------
          // 外部リンク（参照サイトカード）
          // ----------------------------------------
          if (isExternalLink(link.href)) {
            /*
             data-title
             → 太字表示（記事タイトル）

             data-description
             → 通常テキスト（説明文）

             ※ data-preview は使わない
            */
            const domain = getDomain(link.href)

            return `
              <a
                href="${link.href}"
                target="_blank"
                rel="noopener noreferrer"
                class="source-card"
              >
                <span class="source-card-header">
                  <span class="source-domain">
                    ${domain}
                  </span>
                </span>
              </a>
            `
            /*
            return `
              <a
                href="${link.href}"
                target="_blank"
                rel="noopener noreferrer"
                class="source-card"
              >
                <span class="source-card-header">
                  <span class="source-domain">
                    ${domain}
                  </span>
                </span>
                <span class="source-title">
                  読み込み中...
                </span>
                <span class="source-description">
                  記事情報を取得しています...
                </span>
              </a>
            `
*/
          }

          // ----------------------------------------
          // fallback
          // ----------------------------------------
          return `
            <a
              href="${link.href}"
              target="_blank"
              rel="noopener noreferrer"
            >
              ${link.text || link.href}
            </a>
          `
        }

        //text = text.replaceAll('```', '')
        let parsed = marked(text, { renderer: renderer })

        // format Bing's source links more nicely
        // 1. replace "[^1^]" with "[1]" (during progress streams)
        parsed = parsed.replace(/\[\^(\d+)\^]/g, '<strong>[$1]</strong>')
        // 2. replace "^1^" with "[1]" (after the progress stream is done)
        parsed = parsed.replace(/\^(\d+)\^/g, '<strong>[$1]</strong>')

        const sanitizedHtml = DOMPurify.sanitize(parsed, {
          ADD_TAGS: ['iframe'],
          ADD_ATTR: [
            'allow',
            'allowfullscreen',
            'frameborder',
            'scrolling',
            'srcdoc',
            'target',
            'rel',
            'class',
            'href',
            'data-preview',
          ],
        })
        const container = document.createElement('div')
        container.innerHTML = sanitizedHtml
        // Prismでハイライト
        Prism.highlightAllUnder(container)
        // 🟢 HTMLを返す
        return container.innerHTML
      } catch (err) {
        console.error('ERROR', err)
        return null
      }
    },
    async startConversation() {
      try {
        // スクロールを末尾にまで移動させる
        this.isHistoryLoaded = true
        const response = await HTTPService.get_conversation(this.conversationId)
        for (const message of response.data) {
          const id = message.id
          const text = message.text
          const is_user = message.is_user
          const is_preparing = false
          this.messages.push({ id, text, is_user, is_preparing })
          // this.addMessage(message.text, message.is_user)
        }

        if (this.$store.state.conversationStatus?.data) {
          const data = this.$store.state.conversationStatus.data

          const matched = data.find(
            (item) =>
              item.bot_id === this.bot_id &&
              item.conversation_id === this.conversationId &&
              item.status === 'processing',
          )

          if (matched) {
            this.messages.push({
              id: 'force-message',
              text: '',
              is_user: false,
              is_preparing: false,
              is_deep_research: true,
            })
          }
        }
      } catch (error) {
        console.log(error)
      }
    },

    autoResize() {
      const textarea = this.$refs.messageArea.querySelector('textarea')
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    },

    openFileBrowser() {
      this.$refs.fileInput.click()
    },

    setDefaultPrompt() {
      this.promptValue = this.getDefaultPrompt
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files)
      this.validateAndAttachFiles(files)
    },

    handleDrop(event) {
      event.preventDefault()
      const files = Array.from(event.dataTransfer.files)
      this.validateAndAttachFiles(files)
    },

    async handleOfficeFileDownload(messageId, type) {
      this.isCreatingChatbot = true

      try {
        const response = await HTTPService.office_file_download(messageId, type)

        if (response && response.status === 401) {
          localStorage.setItem('expired', 'true')
          window.location.href = '/login'
          return
        }

        const data = response.data
        await HTTPService.fetchAndDownload(data.s3_url, data.filename)
        await HTTPService.delete_office_file(data.filepath)

        this.isCreatingChatbot = false
      } catch (error) {
        console.error(error)

        // 401 認証切れ
        if (error.response?.status === 401) {
          localStorage.setItem('expired', 'true')
          window.location.href = '/login'
          return
        }

        // APIエラーメッセージ優先
        if (error.response?.data?.error) {
          this.errorMessage = error.response.data.error
        } else {
          this.errorMessage =
            '資料の生成に失敗しました。時間を空けて再実行してください。'
        }

        this.showErrorPopup = true
      } finally {
        this.isCreatingChatbot = false
      }
    },

    validateAndAttachFiles(files) {
      const allowedTypes = [
        'application/pdf',
        'text/csv',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'image/jpeg',
        'image/png',
      ]

      for (let file of files) {
        // MB換算
        const fileSizeMB = file.size / (1024 * 1024)
        // 画像かどうか判定
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
        // 上限切り替え
        const maxSize = isImage ? this.maxPerImageFileMB : this.maxPerFileMB

        if (fileSizeMB > maxSize) {
          this.errorMessage = isImage
            ? `・「${file.name}」はサイズが大きすぎます。\r\n・画像は最大 ${this.maxPerImageFileMB}MB までアップロード可能です。\r\n・圧縮して再度お試しください。`
            : `・「${file.name}」はサイズが大きすぎます。\r\n・アップロードは１ファイル最大 ${this.maxPerFileMB}MBまでとなります。\r\n・ファイルを圧縮するか、分割してお試しください。`
          this.showErrorPopup = true
          return
        }

        if (!allowedTypes.includes(file.type)) {
          this.errorMessage =
            'ファイルの読み込むことができませんでした。\r\n' +
            '以下のファイル形式に対応しています。\r\n' +
            '「pdf, csv, txt, doc, docx, xls, xlsx, ppt, pptx, png, jpg, jpeg」'
          this.showErrorPopup = true
          return
        }
      }

      this.attachedFiles.push(...files)
      //this.$refs.fileInput.value = ''
    },

    handleDragOver(event) {
      event.preventDefault()
    },

    removeFile(index) {
      this.attachedFiles.splice(index, 1)
    },

    isImageFile(file) {
      return file.type.startsWith('image/')
    },

    getFilePreview(file) {
      return URL.createObjectURL(file)
    },

    getFileExtension(file) {
      return file.name.split('.').pop().toUpperCase()
    },

    handleSendMessage(messageText) {
      this.userInput = messageText
      this.sendMessage()
    },

    async sendMessage() {
      // Deep Researchは添付ファイルは不可
      if (this.selectedModelLabel?.toLowerCase().includes('deep research')) {
        if (this.attachedFiles.length > 0) {
          this.errorMessage =
            'Deep Researchではファイル添付をご利用いただけません。\r\n恐れ入りますが、メッセージ入力のみでお試しください。'
          this.showErrorPopup = true
          return
        }
      }

      if (!this.userInput && this.attachedFiles.length === 0) return
      this.isDefaultCard = false
      // 自動スクロールを停止
      this.isHistoryLoaded = false
      if (!this.bot_id) {
        // Create chatbot name
        if (this.attachedFiles.length > 0) {
          this.botName = `${this.attachedFiles[0].name}`
        } else {
          // Extract the first sentence or the first 20 characters, whichever is shorter
          const firstSentenceEnd =
            this.userInput.indexOf('.') !== -1
              ? this.userInput.indexOf('.') + 1
              : this.userInput.length
          const firstSentence = this.userInput.slice(0, firstSentenceEnd)

          // Limit to the first 20 characters if needed
          this.botName =
            firstSentence.length > 10
              ? firstSentence.slice(0, 20)
              : firstSentence
        }
      }

      // 1. Create or update bot with current files
      if (!this.bot_id || this.attachedFiles.length > 0) {
        await this.createChatbot()
      }

      // Extract file names and encode them
      const fileNames = this.attachedFiles.map((file) =>
        encodeURIComponent(file.name),
      )

      // Join the file names into a single string
      const filesParam = fileNames.join(',')
      // Clear the attached files
      this.attachedFiles = []

      if (this.userInput) {
        this.addMessage(this.userInput, true)
        this.messageIndex++
      }

      const url = `${process.env.VUE_APP_API_URL}/conversation-by-main-chat`

      try {
        //let token = StorageService.getToken()
        const auth = useAuthStore()

        if (this.isWebSearch === true) {
          this.selectedModel = 'gpt-5.6-terra'
        }

        const requestBody = {
          bot_id: this.bot_id,
          user_input: this.userInput,
          conversation_id: this.conversationId,
          username: this.$store.state.email,
          prompt: this.promptValue + this.selectedPersonality,
          model: this.selectedModel,
          showRagResults: true,
          files: filesParam,
          fileResults: this.fileResults,
          isWebSearch: this.isWebSearch,
          isDeepResearch:
            this.selectedModelLabel?.toLowerCase().includes('deep research') ??
            false,
          effort: this.selectedReasoningEffort,
        }

        this.abortController = new AbortController()
        this.isStreaming = true

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.token}`,
            Accept: 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: this.abortController.signal,
        })

        if (response && response.status === 401) {
          localStorage.setItem('expired', 'true')
          window.location.href = '/login'
          return
        }

        if (!response.ok) {
          const errorBody = await response.json()
          this.errorMessage =
            errorBody.error ||
            `HTTP error! status: ${response.status} ${response.statusText}`
          this.showErrorPopup = true
          throw new Error(this.errorMessage)
        }

        window.gtag('event', 'API', {
          event_name: '相談しチャオ',
          model: this.selectedModelLabel,
          personality: this.selectedPersonalityLabel,
          files: !!filesParam,
          id: this.$store.state.userId,
        })

        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        let buffer = ''
        let isProcessingJson = true
        this.fileResults = ''

        // Initialize the message
        this.addMessage('', false)

        await this.getChatbots()
        //await this.startConversation()

        if (this.selectedModelLabel?.toLowerCase().includes('deep research')) {
          this.isStreaming = false
          this.$store.commit('updateTriggerPolling', true)
        }

        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (this.abortController.signal.aborted) {
            break
          }

          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          if (isProcessingJson) {
            const jsonEndIndex = buffer.indexOf('\r\n--frame\r\n')
            if (jsonEndIndex !== -1) {
              const jsonPart = buffer.slice(0, jsonEndIndex)
              const match = jsonPart.match(/\{[\s\S]*\}/)
              if (match) {
                this.ragResult = JSON.parse(match[0]).vdb_results
              }
              buffer = buffer.slice(jsonEndIndex + 8) // 8 is the length of '\r\n--frame'
              isProcessingJson = false
            }
          } else {
            const lines = buffer.split('\n')
            buffer = lines.pop() || '' // Keep the last incomplete line in the buffer

            for (const line of lines) {
              if (line.startsWith('data:')) {
                const data = line.slice(6)

                if (data === '[DONE]') {
                  this.addMessage('', false, false, this.assistantMessageId)
                  this.isStreaming = false
                  this.abortController = null
                  this.messageIndex++
                  return // End of stream
                }

                // assistant_message_idを含むJSONか確認
                try {
                  const json = JSON.parse(data)

                  // ここがポイント（エラー検知）
                  if (json.error) {
                    this.errorMessage = json.message
                    this.showErrorPopup = true

                    this.isStreaming = false
                    reader.cancel() // ストリーム停止
                    return
                  }

                  if (json.assistant_message_id) {
                    this.assistantMessageId = json.assistant_message_id
                    continue // 表示せずスキップ（チャットには表示しない）
                  }
                } catch (e) {
                  // dataがJSONでない場合は無視して通常処理
                }

                this.addMessage(data, false)
              }
            }
          }
        }
      } catch (error) {
        console.error(error)
        if (error.name === 'AbortError') {
          console.log('stream stopped')
          this.addMessage('', false, false)
          this.isStreaming = false
          this.abortController = null
          this.messageIndex++
          return
        }
        if (error.response && error.response.data.error) {
          this.errorMessage = error.response.data.error
          this.showErrorPopup = true
        } else {
          this.errorMessage =
            'ただいま、選択された言語モデルにアクセス集中しています。<br />回答の生成に通常より時間がかかっています。<br />しばらくお待ちください。'
          this.showErrorPopup = true
        }
      }
    },

    cleanupObjectURLs() {
      this.messages.forEach((message) => {
        if (message.file && message.file.preview) {
          URL.revokeObjectURL(message.file.preview)
        }
      })
    },

    // Add this to clean up object URLs when the component is destroyed
    beforeDestroy() {
      this.cleanupObjectURLs()
    },

    async getConversationHistory(chatbotId) {
      try {
        const response = await HTTPService.get_conversation_history(chatbotId)
        if (response.data && response.data.length > 0) {
          this.bot_id = response.data[0].bot_id
          this.conversationId = response.data[0].conversation_id
          this.messages = []
          this.isDefaultCard = false
          // 会話の詳細を取得
          await this.startConversation()
        } else {
          this.bot_id = chatbotId
          this.conversationId = uuidv4()
          this.messages = []
          this.isDefaultCard = false
        }
      } catch (error) {
        console.error(error)
      }
    },

    async getChatbots() {
      try {
        const chatbots = await HTTPService.get_chatbots(NAV_MASTER.MAIN_CHAT)
        // createdの降順でソート
        chatbots.data.sort((a, b) => new Date(b.created) - new Date(a.created))
        // console.log(chatbots.data)
        this.chatbots = chatbots.data
      } catch (error) {
        console.log(error)
        this.$emit('change-active-component', {
          component: 'Login',
        })
      }
    },

    async createChatbot() {
      this.showModal = false
      const formData = new FormData()
      if (this.attachedFiles) {
        for (let i = 0; i < this.attachedFiles.length; i++) {
          formData.append('files[]', this.attachedFiles[i])
        }
      }
      if (this.botName) {
        formData.append('data', JSON.stringify({ bot_name: this.botName }))
      }
      if (this.bot_id) {
        formData.append('bot_id', this.bot_id)
      }

      // 相談しチャオ
      formData.append('nav_id', NAV_MASTER.MAIN_CHAT)

      if (this.bot_id) this.isUpdatingChatbot = true
      else this.isCreatingChatbot = true
      await HTTPService.createChatbotByMainChat(formData)
        .then((response) => {
          this.showConfirmation = true
          this.bot_id = response.data.bot_id
          this.fileResults = response.data.file_results
          this.getChatbots()
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            return
          }
          if (error.response && error.response.data.error) {
            this.errorMessage = error.response.data.error

            if (this.errorMessage === 'No objects to concatenate') {
              this.errorMessage =
                'ただいま、選択された言語モデルにアクセス集中しています。<br />回答の生成に通常より時間がかかっています。<br />しばらくお待ちください。'
            }

            this.showErrorPopup = true
            if (
              error.response &&
              (error.response.status === 423 || error.response.status === 424)
            ) {
              this.planerror = true
            }
          } else {
            this.errorMessage =
              'ファイルの読み込むことができませんでした。\r\n' +
              '以下のファイル形式に対応しています。\r\n' +
              '「pdf, csv, txt, doc, docx, xls, xlsx, ppt, pptx」'
            this.showErrorPopup = true
          }
        })
        .finally(() => {
          this.isCreatingChatbot = false
          this.isUpdatingChatbot = false
        })
    },

    async deleteChatbot() {
      try {
        const data = {
          bot_ids: this.delete_bot_id,
        }
        this.isCreatingChatbot = true
        await HTTPService.deleteChatbotByMainChat(data)
        await this.getChatbots()
      } catch (error) {
        console.log(error)
      } finally {
        this.handleInitChat()
        this.isDeleteMode = false
        this.isCreatingChatbot = false
        this.showConfirmDeleteBotPopup = false
      }
    },

    addMessage(text, is_user, is_preparing = true, id = '') {
      let is_deep_research =
        this.selectedModelLabel?.toLowerCase().includes('deep research') ??
        false

      if (this.messageIndex >= 0 && is_user === false) {
        // if first message then just push
        if (!this.messages[this.messageIndex]) {
          this.messages.push({
            id: id,
            text: '',
            is_user: is_user,
            is_preparing: is_preparing,
            is_deep_research: is_deep_research,
            file: null,
            progress: 0,
            progressText: '',
            _timer: null,
          })
        } else {
          this.messages[this.messageIndex].id = id
          this.messages[this.messageIndex].text += text
          this.messages[this.messageIndex].is_user = is_user
          this.messages[this.messageIndex].is_preparing = is_preparing
          this.messages[this.messageIndex].is_deep_research = is_deep_research
          this.messages[this.messageIndex].progress = 0
          this.messages[this.messageIndex].progressText = ''
          this.messages[this.messageIndex]._timer = null
        }
      } else {
        this.messageIndex = this.messages.length

        this.messages.push({
          id,
          text,
          is_user,
          is_preparing: false,
          is_deep_research: is_deep_research,
          file: null,
          progress: 0,
          progressText: '',
          _timer: null,
        })

        this.messageIndex = this.messages.length - 1
      }

      // Scroll to the bottom of the chatbot conversation
      //this.$refs.chatbotConversation.scrollTop =
      //  this.$refs.chatbotConversation.scrollHeight
    },

    closeErrorPopup() {
      this.showErrorPopup = false
      this.errorMessage = ''
    },

    closeConfirmDeleteBotPopup() {
      this.showConfirmDeleteBotPopup = false
    },

    /*
     * source-card を探して
     * Python内部APIへURLを渡す
     */
    async fetchLinkPreview() {
      const cards = document.querySelectorAll('.source-card')
      console.log(cards)
      for (const card of cards) {
        const url = card.getAttribute('href')
        console.log('url:' + url)
        //if (!url) continue

        /*
        if (!url) continue

        if (card.dataset.loaded === 'true') {
          continue
        }

        try {
          card.dataset.loaded = 'loading'

          const res = await fetch('/api/link-preview', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              url: url,
            }),
          })

          const data = await res.json()

          const title =
            data.title || '記事タイトルを取得できませんでした'

          const description =
            data.description || '説明文を取得できませんでした'

          const titleEl = card.querySelector('.source-title')
          const descEl = card.querySelector('.source-description')

          if (titleEl) {
            titleEl.textContent = title
          }

          if (descEl) {
            descEl.textContent = description
          }

          card.dataset.loaded = 'true'

        } catch (error) {
          console.error('リンクプレビュー取得失敗:', error)
          card.dataset.loaded = 'error'
        }
        */
      }
    },
  },
}
</script>

<style scoped>
.chatbot-message div {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  max-width: 80%;
}

.chatbot-message div:last-child {
  margin-bottom: 0;
}

.chatbot-message.user-message div {
  color: #fff;
}

input[type='text'] {
  flex: 1;
  margin-right: 10px;
  padding: 10px 15px;
  border-radius: 5px;
  border: 1px solid #ccc;
  line-height: normal;
  height: 50px;
}

button {
  padding: 12px 17px;
  border-radius: 5px;
  border: none;
  color: #fff;
}

button:hover {
  cursor: pointer;
  background-color: #0089b9 !important;
}

p {
  margin-bottom: 0px;
  font-size: 16px;
}

.model-selector select {
  padding: 5px 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background-color: white;
  font-size: 14px;
}

.message-area textarea,
.chatbot-input textarea {
  width: 100%;
  resize: none;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 10px 15px;
  font-size: 16px;
  max-height: 150px;
  overflow-y: auto;
  border: none;
}

textarea:focus,
input:focus {
  outline: none;
}
</style>
